"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import { usePendingSyncResources } from "./use-sync-status"

const CYCLE_INTERVAL_MS = 2200

/**
 * Round-robin through whichever offline-first resources are still pulling.
 * The displayed resource changes ONLY on the timer tick — never directly in
 * response to Dexie writes — so consumers get a clean ~2.2s rhythm even
 * though `_syncMeta` is hammered with updates during cold start (17
 * resources writing status / lastSyncId in parallel).
 *
 * `pending` from `usePendingSyncResources` is a fresh array on every Dexie
 * write, so reading it directly inside the memo would recompute `current`
 * on each write — and as the list shrinks between ticks the modulo index
 * would land on a *different* resource, producing jerky mid-cycle cuts.
 * Holding the latest list in a ref decouples the rotation cadence from the
 * reactivity cadence.
 *
 * Returns:
 * - `current` — the resource name to display right now, or `null` if nothing
 *   is pending (consumer should hide the loader).
 * - `pending` — the full pending list (callers may want the count, e.g. to
 *   skip the rotation when only one resource remains).
 */
export function useCyclingPendingResource(resourceNames: readonly string[]): {
  current: string | null
  pending: string[]
} {
  const pending = usePendingSyncResources(resourceNames)
  const pendingRef = useRef(pending)
  pendingRef.current = pending

  const [tick, setTick] = useState(0)

  useEffect(() => {
    const id = setInterval(() => {
      setTick((t) => t + 1)
    }, CYCLE_INTERVAL_MS)
    return () => clearInterval(id)
  }, [])

  const current = useMemo(() => {
    const list = pendingRef.current
    if (list.length === 0) return null
    return list[tick % list.length] ?? list[0] ?? null
    // tick is the only legitimate trigger for recomputation here — pendingRef
    // is intentionally read fresh without participating in deps so that
    // shrinking-list updates wait for the next scheduled tick instead of
    // mid-cycle reshuffles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick])

  return { current, pending }
}
